import { Divider, Typography, useTheme, List, ListItem, ListItemText, Box, Collapse, IconButton } from '@mui/material'
import { black, grey } from '../../../colors'
import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { UserManager } from '../../../APIManager'
import GenericModal from '../../../components/GenericModal'
import { notification } from '../../../components/Notifications'
import { ButtonType } from '../../../components/GenericButton/styles'
import type { IEnrollment } from '../../../models/Enrollment'
import type { FC } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

interface UpdateEnrollmentsProps {
  newlyEnrolled: any[]
  newlyUnenrolled: any[]
  isConfirmModalOpen: boolean
  setIsConfirmModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSubmit: () => void
  sectionId: string
  studentRoleId: string
}

export const UpdateEnrollments: React.FC<UpdateEnrollmentsProps> = ({
  newlyEnrolled,
  newlyUnenrolled,
  isConfirmModalOpen,
  setIsConfirmModalOpen,
  handleSubmit,
  sectionId,
  studentRoleId
}) => {
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [enrolledOpen, setEnrolledOpen] = useState(true)
  const [unenrolledOpen, setUnenrolledOpen] = useState(true)
  const multipleChanges = newlyEnrolled.length + newlyUnenrolled.length > 1

  const addMutation = useMutation({
    mutationFn: async (enrollment: IEnrollment) => {
      const userId = enrollment?.userId || ''
      return (await UserManager.postStudentRoleByIdApi(userId, { email: enrollment?.email, objectId: sectionId, roleId: studentRoleId, instructors: [] })).data || []
    },
    onError: (error: unknown) => {
      console.error('Error adding student role:', error)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (enrollment: IEnrollment) => {
      const userId = enrollment?.userId || ''
      return (await UserManager.deleteStudentRoleByIdAndRoleIdApi(userId, studentRoleId, { sectionId })).data || []
    },
    onError: (error: unknown) => {
      console.error('Error deleting student role:', error)
    }
  })

  const handleCloseModal = (): void => { setIsConfirmModalOpen(false) }

  const handleUpdate = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await Promise.all([
        ...newlyEnrolled.map(async enrollment => await addMutation.mutateAsync(enrollment)),
        ...newlyUnenrolled.map(async enrollment => await deleteMutation.mutateAsync(enrollment))
      ])
      setIsConfirmModalOpen(false)
    } catch (error) {
      notification('An error occurred while updating enrollments. Please Try Again', { variant: 'error' })
    } finally {
      setIsLoading(false)
      handleCloseModal()
      handleSubmit()
    }
  }

  return (
    <GenericModal
      isOpen={isConfirmModalOpen}
      onClose={handleCloseModal}
      onConfirm={handleUpdate}
      title="Updating Enrollments"
      modalWidth={theme.spacing(136)}
      confirmButtonLoading={isLoading}
      destructiveButtonType={ButtonType.Secondary}
    >

      <Typography mt={4} mb={3} fontSize={theme.spacing(3.5)} lineHeight={theme.spacing(5)} color={grey[850]}>
        You are about to change the enrollment status of {multipleChanges ? 'multiple students' : 'a student'}. Do you wish to continue?
      </Typography>
      <Box display="flex" alignItems="center" mt={3}>
        <Typography variant='h5' fontSize={theme.spacing(4)} color={black[100]} sx={{ flexGrow: 1 }}>
          Newly Enrolled in Course Section ({newlyEnrolled.length})
        </Typography>
        <IconButton size="small" onClick={() => { setEnrolledOpen(o => !o) }}>
          {enrolledOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={enrolledOpen} timeout="auto" unmountOnExit>
        <Box mb={3}>
          <EnrollmentList students={newlyEnrolled} emptyText="No new enrollments." />
        </Box>
      </Collapse>
      <Divider sx={{ my: 2 }} />
      <Box display="flex" alignItems="center" mt={3}>
        <Typography variant='h5' fontSize={theme.spacing(4)} color={black[100]} sx={{ flexGrow: 1 }}>
          Unenrolled from Course Section ({newlyUnenrolled.length})
        </Typography>
        <IconButton size="small" onClick={() => { setUnenrolledOpen(o => !o) }}>
          {unenrolledOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={unenrolledOpen} timeout="auto" unmountOnExit>
        <Box mb={3}>
          <EnrollmentList students={newlyUnenrolled} emptyText="No unenrollments." />
        </Box>
      </Collapse>

    </GenericModal>
  )
}

const EnrollmentList: FC<{ students: any[], emptyText: string }> = ({ students, emptyText }) => {
  const theme = useTheme()
  if (!students || students.length === 0) {
    return <Typography color={grey[600]} fontSize={theme.spacing(3)}>{emptyText}</Typography>
  }
  return (
    <List dense>
      {students?.map((e, idx) => (
        <ListItem key={e.userId || idx} sx={{ mb: 1 }}>
          <ListItemText
            primary={<span style={{ fontWeight: 500 }}>{e.firstName} {e.lastName}</span>}
            secondary={e.email}
            primaryTypographyProps={{ style: { fontSize: theme.spacing(3.2), color: black[100] } }}
            secondaryTypographyProps={{ style: { fontSize: theme.spacing(3), color: grey[600] } }}
          />
        </ListItem>
      ))}
    </List>
  )
}
